// src/generic/services/CloudflareR2Upload.js
import React, { useEffect, useId, useRef, useState } from "react";

const PUBLIC_R2_BASE_URL = "https://pub-a16d3f6629df4826a88f5d3924bec10e.r2.dev";
const SIGNED_UPLOAD_FUNCTION_URL =
  "https://us-central1-vdopage-bb3af.cloudfunctions.net/getSignedUploadUrl";

const createR2ObjectKey = (file) => {
  const safeName = file.name
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-zA-Z0-9._-]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .toLowerCase();

  return `logos/${Date.now()}-${safeName || "logo"}`;
};

const getPublicR2Url = (objectKey) =>
  `${PUBLIC_R2_BASE_URL}/${objectKey.split("/").map(encodeURIComponent).join("/")}`;

function CloudflareR2Upload({ setImageUrl, onUploadStateChange }) {
  const inputId = useId();
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [previewUrl, setPreviewUrl] = useState("");

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const uploadFile = async (selectedFile) => {
    if (!selectedFile) return setError("Select a logo before uploading.");
    if (!selectedFile.type.startsWith("image/")) {
      setError("Logo must be an image file.");
      return undefined;
    }

    setError("");
    setLoading(true);
    onUploadStateChange?.(true);

    try {
      const objectKey = createR2ObjectKey(selectedFile);
      const query = new URLSearchParams({
        filename: objectKey,
        contentType: selectedFile.type,
      });

      const response = await fetch(`${SIGNED_UPLOAD_FUNCTION_URL}?${query.toString()}`);
      if (!response.ok) throw new Error("Could not start the upload. Please try again.");

      const { url } = await response.json();
      const uploadRes = await fetch(url, {
        method: "PUT",
        headers: { "Content-Type": selectedFile.type },
        body: selectedFile,
      });

      if (!uploadRes.ok) throw new Error(`Upload failed (${uploadRes.status}). Please try again.`);

      const publicUrl = getPublicR2Url(objectKey);
      setImageUrl(publicUrl);
      setFile(null);
      setPreviewUrl("");
      if (inputRef.current) inputRef.current.value = "";
      console.info("LOGO_UPLOAD_SUCCEEDED public_url=%s object_key=%s", publicUrl, objectKey);
      return publicUrl;
    } catch (err) {
      console.error("LOGO_UPLOAD_FAILED", err);
      setError(`Upload failed: ${err.message}`);
      return undefined;
    } finally {
      setLoading(false);
      onUploadStateChange?.(false);
    }
  };

  const handleFileChange = (e) => {
    const selected = e.target.files[0];
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setFile(selected || null);
    setPreviewUrl(selected ? URL.createObjectURL(selected) : "");
    setError("");
    if (selected) uploadFile(selected);
  };

  const handleUpload = () => uploadFile(file);

  const handleRemove = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setFile(null);
    setPreviewUrl("");
    setError("");
    setImageUrl("");
    if (inputRef.current) inputRef.current.value = "";
  };

  return (
    <div className="space-y-4">
      {previewUrl && (
        <div className="flex items-center gap-4 rounded-lg border border-slate-200 bg-white p-3">
          <img
            src={previewUrl}
            alt="Logo preview"
            className="h-16 w-24 rounded-md border border-slate-200 bg-slate-50 object-contain p-2"
          />
          <p className="min-w-0 flex-1 truncate text-sm font-semibold text-slate-900">
            {file?.name}
          </p>
          <button
            type="button"
            className="ghost-button text-rose-600 hover:text-rose-700"
            onClick={handleRemove}
          >
            Remove
          </button>
        </div>
      )}

      <div className="grid gap-3">
        <label
          className="flex cursor-pointer flex-col items-center justify-center rounded-lg border border-dashed border-slate-300 bg-white px-4 py-6 text-center hover:border-slate-400"
          htmlFor={inputId}
        >
          <span className="text-sm font-semibold text-slate-900">
            Logo file
          </span>
          <span className="mt-1 text-xs text-slate-500">
            PNG, JPG, or SVG. Click to choose your logo image.
          </span>
        </label>
        <input
          type="file"
          id={inputId}
          ref={inputRef}
          accept="image/*"
          onChange={handleFileChange}
          className="sr-only"
        />
        <button
          type="button"
          className="secondary-button w-full"
          onClick={handleUpload}
          disabled={loading || !file}
        >
          {loading ? "Uploading..." : "Upload logo"}
        </button>
      </div>

      {error && <div className="text-sm font-medium text-rose-600">{error}</div>}
    </div>
  );
}

export default CloudflareR2Upload;
